/**
 * Matches backend {@code AnomalyType} enum values + palette convention.
 */
export const ANOMALY_TYPE_COLOR_BY_ENUM: Record<number, string> = {
  1: '#1f78b4', // Flooding
  2: '#a6cee3', // Overwatering
  3: '#e31a1c', // Drought
  4: '#ff7f00', // SkippingOfSeedlings
  5: '#b2df8a', // PrematureAging
  6: '#6a3d9a', // NutritionalStress
  7: '#4f46e5', // ComplexAnomaly
  // codeFromAiServices types (other system).
  0: '#1E88E5', // Open Water
  101: '#616161', // Known Road
  102: '#795548', // Known Building
  103: '#FF9800', // Potential Construction / Development Zone
  104: '#9E9E9E', // Probable Road
  105: '#A1887F', // Probable Building
  106: '#607D8B', // Undefined Artificial Object
};

export const ANOMALY_LEGEND_ITEMS: readonly {anomalyType: number; labelKey: string}[] = [
  {anomalyType: 1, labelKey: 'ANOMALY_TYPE_FLOODING'},
  {anomalyType: 2, labelKey: 'ANOMALY_TYPE_OVERWATERING'},
  {anomalyType: 3, labelKey: 'ANOMALY_TYPE_DROUGHT'},
  {anomalyType: 4, labelKey: 'ANOMALY_TYPE_SKIPPING_OF_SEEDLINGS'},
  {anomalyType: 5, labelKey: 'ANOMALY_TYPE_PREMATURE_AGING'},
  {anomalyType: 6, labelKey: 'ANOMALY_TYPE_NUTRITIONAL_STRESS'},
  {anomalyType: 7, labelKey: 'ANOMALY_TYPE_COMPLEX_ANOMALY'},
  {anomalyType: 0, labelKey: 'ANOMALY_TYPE_OPEN_WATER'},
  {anomalyType: 101, labelKey: 'ANOMALY_TYPE_KNOWN_ROAD'},
  {anomalyType: 102, labelKey: 'ANOMALY_TYPE_KNOWN_BUILDING'},
  {anomalyType: 103, labelKey: 'ANOMALY_TYPE_POTENTIAL_CONSTRUCTION'},
  {anomalyType: 104, labelKey: 'ANOMALY_TYPE_PROBABLE_ROAD'},
  {anomalyType: 105, labelKey: 'ANOMALY_TYPE_PROBABLE_BUILDING'},
  {anomalyType: 106, labelKey: 'ANOMALY_TYPE_UNDEFINED_ARTIFICIAL_OBJECT'},
];

/**
 * AI-services anomaly types ({@code codeFromAiServices}). Distinct from {@link ANOMALY_TYPE_COLOR_BY_ENUM}
 * because codes 3/6/7 collide numerically with the old {@code codeFromEnum} but mean other things.
 */
export const ANOMALY_AI_TYPE_LABEL_BY_CODE: Record<number, string> = {
  0: 'ANOMALY_TYPE_OPEN_WATER',
  3: 'ANOMALY_TYPE_SEVERE_DROUGHT',
  6: 'ANOMALY_TYPE_CHLOROSIS',
  7: 'ANOMALY_TYPE_DEPRESSED_VEGETATION',
  101: 'ANOMALY_TYPE_KNOWN_ROAD',
  102: 'ANOMALY_TYPE_KNOWN_BUILDING',
  103: 'ANOMALY_TYPE_POTENTIAL_CONSTRUCTION',
  104: 'ANOMALY_TYPE_PROBABLE_ROAD',
  105: 'ANOMALY_TYPE_PROBABLE_BUILDING',
  106: 'ANOMALY_TYPE_UNDEFINED_ARTIFICIAL_OBJECT',
};

export const ANOMALY_AI_TYPE_COLOR_BY_CODE: Record<number, string> = {
  0: '#1E88E5',
  3: '#D32F2F',
  6: '#FDD835',
  7: '#8BC34A',
  101: '#616161',
  102: '#795548',
  103: '#FF9800',
  104: '#9E9E9E',
  105: '#A1887F',
  106: '#607D8B',
};

function hasValue(v: unknown): boolean {
  return v !== null && v !== undefined && String(v).trim() !== '';
}

/** Label key for a FieldAnomaly row: prefers {@code codeFromEnum}, falls back to {@code codeFromAiServices}. */
export function resolveFieldAnomalyTypeKey(row: unknown): string | undefined {
  const rec = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
  if (hasValue(rec['codeFromEnum'])) {
    return translateKeyForAnomalyType(rec['codeFromEnum']);
  }
  const ai = Number(rec['codeFromAiServices']);
  return Number.isFinite(ai) ? ANOMALY_AI_TYPE_LABEL_BY_CODE[ai] : undefined;
}

/** Badge color for a FieldAnomaly row: prefers {@code codeFromEnum}, falls back to {@code codeFromAiServices}. */
export function resolveFieldAnomalyColor(row: unknown): string {
  const rec = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
  if (hasValue(rec['codeFromEnum'])) {
    const n = Number(rec['codeFromEnum']);
    return (Number.isFinite(n) && ANOMALY_TYPE_COLOR_BY_ENUM[n]) || '#b91c1c';
  }
  const ai = Number(rec['codeFromAiServices']);
  return (Number.isFinite(ai) && ANOMALY_AI_TYPE_COLOR_BY_CODE[ai]) || '#b91c1c';
}

/** Field parcel outline on anomaly map (stroke). */
export const ANOMALY_FIELD_OUTLINE_STROKE = '#dc2626';

/** Leaflet dash pattern for field outlines. */
export const ANOMALY_FIELD_OUTLINE_DASH = '8, 6';

/** ngx-translate keys for {@link ProductSourceType} (Custom = 0 … Landsat = 2). */
export const PRODUCT_SOURCE_TRANSLATION_KEY: Record<number, string> = {
  0: 'PRODUCT_SOURCE_TYPE_CUSTOM',
  1: 'PRODUCT_SOURCE_TYPE_SENTINEL2',
  2: 'PRODUCT_SOURCE_TYPE_LANDSAT',
};

export function translateKeyForAnomalyType(typeVal: unknown): string | undefined {
  if (typeof typeVal === 'string' && typeVal.trim()) {
    const normalized = normalizeEnumLikeToken(typeVal);
    const byName: Record<string, string> = {
      FLOODING: 'ANOMALY_TYPE_FLOODING',
      OVERWATERING: 'ANOMALY_TYPE_OVERWATERING',
      DROUGHT: 'ANOMALY_TYPE_DROUGHT',
      SKIPPING_OF_SEEDLINGS: 'ANOMALY_TYPE_SKIPPING_OF_SEEDLINGS',
      PREMATURE_AGING: 'ANOMALY_TYPE_PREMATURE_AGING',
      NUTRITIONAL_STRESS: 'ANOMALY_TYPE_NUTRITIONAL_STRESS',
      COMPLEX_ANOMALY: 'ANOMALY_TYPE_COMPLEX_ANOMALY',
      OPEN_WATER: 'ANOMALY_TYPE_OPEN_WATER',
      // Codes 3/6/7 collide numerically with old enum; resolve distinctly by AI enum name.
      SEVERE_DROUGHT: 'ANOMALY_TYPE_SEVERE_DROUGHT',
      CHLOROSIS: 'ANOMALY_TYPE_CHLOROSIS',
      DEPRESSED_VEGETATION: 'ANOMALY_TYPE_DEPRESSED_VEGETATION',
      KNOWN_ROAD: 'ANOMALY_TYPE_KNOWN_ROAD',
      KNOWN_BUILDING: 'ANOMALY_TYPE_KNOWN_BUILDING',
      // Code 103 (Potential Construction / Development Zone).
      POTENTIAL_CONSTRUCTION: 'ANOMALY_TYPE_POTENTIAL_CONSTRUCTION',
      POTENTIAL_CONSTRUCTION_DEVELOPMENT_ZONE: 'ANOMALY_TYPE_POTENTIAL_CONSTRUCTION',
      CONSTRUCTION_ZONE: 'ANOMALY_TYPE_POTENTIAL_CONSTRUCTION',
      DEVELOPMENT_ZONE: 'ANOMALY_TYPE_POTENTIAL_CONSTRUCTION',
      // Code 104 (Probable Road) — server enum name uses "Potential".
      PROBABLE_ROAD: 'ANOMALY_TYPE_PROBABLE_ROAD',
      POTENTIAL_ROAD: 'ANOMALY_TYPE_PROBABLE_ROAD',
      // Code 105 (Probable Building) — server enum name uses "Potential".
      PROBABLE_BUILDING: 'ANOMALY_TYPE_PROBABLE_BUILDING',
      POTENTIAL_BUILDING: 'ANOMALY_TYPE_PROBABLE_BUILDING',
      UNDEFINED_ARTIFICIAL_OBJECT: 'ANOMALY_TYPE_UNDEFINED_ARTIFICIAL_OBJECT',
    };
    if (byName[normalized]) {
      return byName[normalized];
    }
  }
  const n = typeof typeVal === 'number' ? typeVal : Number(typeVal);
  if (!Number.isFinite(n)) {
    return undefined;
  }
  return ANOMALY_LEGEND_ITEMS.find(i => i.anomalyType === n)?.labelKey;
}

export function translateKeyForProductSource(sourceVal: unknown): string | undefined {
  if (typeof sourceVal === 'string' && sourceVal.trim()) {
    const normalized = normalizeEnumLikeToken(sourceVal);
    const byName: Record<string, string> = {
      CUSTOM: 'PRODUCT_SOURCE_TYPE_CUSTOM',
      SENTINEL2: 'PRODUCT_SOURCE_TYPE_SENTINEL2',
      SENTINEL_2: 'PRODUCT_SOURCE_TYPE_SENTINEL2',
      LANDSAT: 'PRODUCT_SOURCE_TYPE_LANDSAT',
    };
    if (byName[normalized]) {
      return byName[normalized];
    }
  }
  const n = typeof sourceVal === 'number' ? sourceVal : Number(sourceVal);
  if (!Number.isFinite(n)) {
    return undefined;
  }
  return PRODUCT_SOURCE_TRANSLATION_KEY[n];
}

export function resolveAnomalyPolygonColor(dto: unknown): string {
  const rec = dto && typeof dto === 'object' ? (dto as Record<string, unknown>) : {};
  const nested = rec['anomalyPolygon'];
  const nestedRec =
    nested && typeof nested === 'object' ? (nested as Record<string, unknown>) : null;
  /** Match {@code row?.anomalyPolygon ?? row} on anomalies-map (gis-anomalies / anomaly-map-manager). */
  const typeRaw =
    rec['anomalyType'] ??
    rec['type'] ??
    rec['codeFromAiServices'] ??
    rec['codeFromEnum'] ??
    nestedRec?.['anomalyType'] ??
    nestedRec?.['type'] ??
    nestedRec?.['codeFromAiServices'] ??
    nestedRec?.['codeFromEnum'];
  const n = typeof typeRaw === 'number' ? typeRaw : Number(typeRaw);
  if (Number.isFinite(n) && ANOMALY_TYPE_COLOR_BY_ENUM[n]) {
    return ANOMALY_TYPE_COLOR_BY_ENUM[n];
  }
  const c = rec['color'] ?? nestedRec?.['color'];
  return typeof c === 'string' && c.trim() ? c : '#b91c1c';
}

function normalizeEnumLikeToken(value: string): string {
  return value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_')
    .toUpperCase();
}
